import * as React from "react";
import { useRef } from "react";
import { Editor } from "@tinymce/tinymce-react";
import { InitOptions } from "@tinymce/tinymce-react/lib/es2015/main/ts/components/Editor";

export interface RichTextEditorQuickLinks {
  videoConsultationUrl?: string;
  bookingAppointmentUrl?: string;
}

interface RichTextEditorProps {
  name: string;
  value?: string;
  className: string;
  disabled?: boolean;
  cssUrl: string;
  onChange: (value: string) => any;
  onTextChange?: (value: string) => any;
  height?: string;
  toolbar?: string;
  quickLinks?: RichTextEditorQuickLinks;
}

const languagePackMapping = {
  en: undefined, // default so not needed
  es: "es",
  fr: "fr_FR",
  de: "de",
  it: "it",
  ja: "ja",
  pl: "pl",
  pt: "pt_PT",
  zh: "zh_TW",
};

const defaultToolbar =
  "undo redo | styles | bold italic underline | fontselect fontsizeselect | link | bullist numlist | forecolor backcolor removeformat | table | charmap emoticons";

const RichTextEditor = (props: RichTextEditorProps) => {
  const editorRef = useRef(null);

  const toolbar = props.disabled ? false : props.toolbar ? props.toolbar : defaultToolbar;

  const getDefaultLinkList = () => {
    if (props.quickLinks) {
      let items = [];
      if (props.quickLinks.bookingAppointmentUrl) {
        items.push({
          title: i18n["RichTextEditor.QuickLinks.BookingAppointment.Name"],
          value: props.quickLinks.bookingAppointmentUrl,
        });
      }
      if (props.quickLinks.videoConsultationUrl) {
        items.push({
          title: i18n["RichTextEditor.QuickLinks.VideoConsultation.Name"],
          value: props.quickLinks.videoConsultationUrl,
        });
      }
      return items;
    }
    return undefined;
  };

  const tinyMCEConfiguration: InitOptions = {
    content_css: props.cssUrl,
    content_style: colorManager.colors,
    inline_styles: false,
    body_class: "rte " + props.className,
    language: languagePackMapping[lang],
    toolbar: toolbar,
    table_toolbar: "",
    table_appearance_options: false,
    menubar: false,
    plugins: "lists table emoticons autolink link charmap",
    elementpath: false,
    formats: {
      title: { block: "div", classes: "title" },
    },
    block_formats: `${i18n["RichTextEditor.Style.Paragraph"]}=p;${i18n["RichTextEditor.Style.Header"]}=title`,
    style_formats: [{ title: `${i18n["RichTextEditor.Style.Header"]}`, format: "title" }],
    table_advtab: false,
    table_row_advtab: false,
    table_cell_advtab: false,
    height: props.height,
    relative_urls: false,
    remove_script_host: false,
    link_list: function (success) {
      success(getDefaultLinkList());
    },
    mobile: {
      toolbar_mode: "floating",
    },
    setup: (editor) => {
      editor.on("BeforeSetContent", (e) => {
        if (e.content.startsWith("<table ")) {
          e.content = '<div class="overflow-table">' + e.content + "</div>";
        }
      });
    },
    paste_as_text: true,
    paste_merge_formats: false,
    paste_data_images: false
  };

  return (
    <div className={"rte-container" + (props.disabled ? " disabled" : "")}>
      <Editor
        onInit={(evt: any, editor) => (editorRef.current = editor)}
        init={tinyMCEConfiguration}
        disabled={props.disabled}
        value={props.value}
        onEditorChange={(value) => {
          props.onChange(value);
          props.onTextChange?.(editorRef.current.getContent({ format: "text" }));
        }}
        id={props.name}
        textareaName={props.name}
      />
    </div>
  );
};

export default RichTextEditor;
